/* eslint-disable max-lines */
import { useSmoothStream } from '@/components/chat/hooks/useSmoothStream';
import type {
  Annotation,
  Citation,
  EvidenceImage,
  Source,
} from '@heidi/evidence/contracts';
import { getCitationKey, shouldMuteKeySafetyNotes } from '@heidi/evidence/contracts';
import type { SessionConversation, TextResult } from '@/components/chat/types/types';
import { ChatMode, isPartnerCitation } from '@/components/chat/types/types';
import {
  useIsEvidenceInlineImagesEnabled,
  useIsMutedKeySafetyNotesEnabled,
} from '@heidi/evidence/flags';
import { useIsUKOrEU } from '@/hooks/useIsUKOrEU';
import { Button } from '@heidi/design-system/button';
import { logger } from '@/utils/logger/logger';
import { extractMessageId } from '../utils/extractMessageId';
import { ChatMessageContext } from '@/context/ChatMessageContext';
import { CitationBadge, type CitationSource } from '@heidi/evidence';

import {
  DrugInteractionWarningBanner,
  EvidenceImageBlock,
  KeyPointsBlock,
  KeySafetyNotes,
  SafetyCallout,
} from '@heidi/evidence';
import { HeidiButton } from '@/components/chat/components/HeidiButton';
import UserNotesBlock from '@/components/chat/components/UserNotesBlock';
import {
  Popover,

  PopoverContent,
  PopoverTrigger,
} from '@heidi/design-system/popover';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import {
  TypographyCaptionBold,
  TypographyP,
  TypographyP2,
} from '@heidi/design-system/typography';
import { SignUpPromptModalChat } from '@/components/modal/sign-up-prompt-modal-chat';
import { useHasPermissionToReplace } from '@/components/ui/ask-ai-input/hooks/useHasPermissionToReplace';
import { toast } from '@heidi/design-system/toast';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { EvidenceSignUpWall } from '@/models/analytics/features/evidence';
import { resolveTimelineErrorDisplay } from '@/utils/chatErrors';
import { useIntl } from 'react-intl';
import { useTranslations } from '@heidi/runtime/i18n';
import { isEmpty } from 'lodash-es';
import {
  BookOpenIcon,
  ChevronDownIcon,
  CopyIcon,
  FilePlus,
  ListPlus,
  Replace,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Streamdown } from 'streamdown';
import {
  densityToCalloutVariant,
  mermaidConfig,
  MermaidStreamingContext,
  normalizeHeadingDepth,
  responseProseClass,
  shikiTheme,
  streamdownPlugins,
  streamdownRehypePlugins,
  useProseDensity,
  type MarkdownComponents,
  type ProseDensity,
} from '@heidi/answer-kit';
import { useChatResponseActions } from '../hooks/useChatResponseActions';
import { getFaviconUrl } from '@/utils/faviconCache';
import { collapseCustomBlockBlankLines } from '@heidi/utils';
import { normalizeKeySafetyNotesTags } from '@heidi/utils';
import { getEnrichedReferences } from '../utils/referenceUtils';
import { AnnotatedText } from './AnnotatedText';
import { ReferencesSection } from '@heidi/evidence';
// Side-effect import: fills the host's slot with the two answer ports only Chat
// may name. Every path that renders an answer surface reaches this module.
import './evidence-answer-ports';
import { CustomerFeedback } from './customer-feedback';
import { track } from '@/lib/analytics/track';
import { getEvidenceMdComponents, mdComponents } from '@/components/markdown/markdown-components';
export type { Citation } from '@heidi/evidence/contracts';
export { getDomainFromUrl, normalizeDomain } from '@heidi/evidence/contracts';
export { getFaviconUrl } from '@/utils/faviconCache';
export { extractMessageId } from '../utils/extractMessageId';

const removeOuterMarkdownCitationParenthesis = (markdown: string): string => {
  return markdown.replace(
    /\(\[([^\]]+)\]\(([^\)]+)\)\)/g,
    (match: string, title: string, url: string) => {
      // Validate URL before fixing
      try {
        new URL(url);
        return `[${title}](${url})`;
      } catch {
        return match;
      }
    }
  );
};

// ===== EVIDENCE MODE CITATION HELPERS =====

interface CitationMarkerData {
  fullText: string;
  citationMap: Map<number, CitationSource[]>;
}

/**
 * Deduplicate citations by appropriate key based on source type.
 * Keeps the first occurrence of each unique citation.
 * EVI-678: Sorts results to prioritize partner sources first.
 */
export const deduplicateCitationsByUrl = (
  citations: CitationSource[]
): CitationSource[] => {
  const seen = new Set<string>();
  const deduplicated = citations.filter((citation) => {
    const key = getCitationKey(citation);

    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });

  // EVI-678: Sort to prioritize partner sources first on citation badges
  return deduplicated.sort((a, b) => {
    const aIsPartner = isPartnerCitation(a.source_type) ? 0 : 1;
    const bIsPartner = isPartnerCitation(b.source_type) ? 0 : 1;
    return aIsPartner - bIsPartner;
  });
};

/**
 * Builds concatenated text with HTML citation markers inserted at citation positions.
 * Markers are self-closing HTML elements that survive markdown parsing via rehype-raw.
 *
 * Example output: "List item content<cite-badge data-idx="0"/> more text"
 */
const buildTextWithCitationMarkers = (chunks: TextResult[]): CitationMarkerData => {
  let fullText = '';
  const citationMap = new Map<number, CitationSource[]>();
  let citationIdx = 0;

  for (const chunk of chunks) {
    const cleanedText = removeOuterMarkdownCitationParenthesis(chunk.text);
    fullText += cleanedText;

    if (chunk.citations?.length) {
      // Insert marker at current position (end of this chunk's text)
      const marker = `<cite-badge data-idx="${citationIdx}"></cite-badge>`;
      fullText += marker;
      // Deduplicate citations by URL before storing in map
      citationMap.set(citationIdx, deduplicateCitationsByUrl(chunk.citations));
      citationIdx++;
    }
  }

  return { fullText, citationMap };
};

// ===== TEXT RESPONSE COMPONENT =====

interface TextResponseProps {
  sessionId: string | null;
  chunks: TextResult[];
  className?: string;
  conversation?: SessionConversation;
  position?: number;
  isStreaming?: boolean;
  onTryWithResearch?: () => void;
  mode?: ChatMode; // The mode that generated this response
  isLastTextResponse?: boolean; // Whether this is the most recent AI response
  isLastInTextGroup?: boolean; // For EVIDENCE mode: only show action bar on last text block
  // EVI-1567 / EV-023: true when this is the last text group of its answer. The
  // V2 block renderer passes isLastInTextGroup={false} (chrome is hoisted to the
  // group level), so this flag is what surfaces the drug-interaction banner at
  // the end of the answer's text, before the references section.
  isLastTextGroupInAnswer?: boolean;
  groupContent?: string; // For EVIDENCE mode: aggregated content from all text blocks in group
  isFullScreenEvidence?: boolean; // Whether the chat is displayed in full-screen mode (vs sidebar)
  chatTitle?: string; // Chat title for document naming when creating new tabs
  /**
   * Prose density for the column this answer renders into — `comfortable` for a
   * wide document column, `compact` for a narrow panel. Defaults from
   * `sessionId` because that is what the two Evidence hosts happen to differ on
   * (the in-session sidebar is narrow, `/scribe/chat` is not), but a host that
   * renders Evidence prose into a panel without a session should pass
   * `compact` explicitly rather than inherit the wide default.
   */
  density?: ProseDensity;
}

// ===== ACTIONS DROPDOWN COMPONENT =====

export interface ActionsPopoverProps {
  onAddToNote?: () => void;
  onReplaceNote?: () => void;
  onCreateNewTab?: () => void;
  isSessionless?: boolean;
}

export const ActionsPopover = ({
  onAddToNote,
  onReplaceNote,
  onCreateNewTab,
  isSessionless = false,
}: ActionsPopoverProps): JSX.Element | null => {
  const { t } = useTranslations();
  const [open, setOpen] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const { hasPermissionToReplace } = useHasPermissionToReplace();
  const { isAuthenticated } = useIsAuthenticated();

  // Hide the entire popover for unauthenticated users on mobile
  if (!isAuthenticated) return null;

  // For logged-out sessionless users, always show the actions dropdown but intercept clicks
  const shouldShowActionsForPreview = isSessionless && !isAuthenticated;

  // Hide popover if no actions are available and not a preview scenario
  const hasAnyActions =
    hasPermissionToReplace  || shouldShowActionsForPreview;
  if (!hasAnyActions) return null;

  const handleAction = (action?: () => void) => {
    // If user is not authenticated in sessionless mode, show signup modal
    if (shouldShowActionsForPreview) {
      track({ options: { amplitude: false },
        event_name: TrackingEventName.EvidenceSignUpWallViewed,
        props: {
          wall_type: EvidenceSignUpWall.ActionsWall,
        },
      });
      setShowSignUpModal(true);
      setOpen(false);
      return;
    }

    action?.();
    setOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="md" aria-label={t('chat.moreActions')}>
            <TypographyCaptionBold className="text-text-secondary">
              {t('chat.actions')}
            </TypographyCaptionBold>
            <ChevronDownIcon className="text-text-secondary" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-1" align="end" side="top" sideOffset={4}>
          <div className="space-y-1">
            {/* For logged-out users, show preview of actions */}
            {shouldShowActionsForPreview ? (
              <>
                <Button size="lg"
                  disableAutomaticSvgSizing
                  onClick={() => handleAction()}
                  variant="ghost"
                  className="w-full justify-start gap-2 text-sm"
                >
                  <ListPlus className="size-4 text-text-tertiary" />
                  <TypographyP2 className="text-text-primary">{t('chat.addToNote')}</TypographyP2>
                </Button>
                <Button size="lg"
                  disableAutomaticSvgSizing
                  onClick={() => handleAction()}
                  variant="ghost"
                  className="w-full justify-start gap-2 text-sm"
                >
                  <Replace className="size-4 text-text-tertiary" />
                  <TypographyP2 className="text-text-primary">{t('chat.replaceNote')}</TypographyP2>
                </Button>
                <Button size="lg"
                  disableAutomaticSvgSizing
                  onClick={() => handleAction()}
                  variant="ghost"
                  className="w-full justify-start gap-2 text-sm"
                >
                  <FilePlus className="size-4 text-text-tertiary" />
                  <TypographyP2 className="text-text-primary">{t('chat.createNewTab')}</TypographyP2>
                </Button>
              </>
            ) : (
              <>
                {/* Add to note/document */}
                {hasPermissionToReplace && (
                  <Button size="lg"
                    disableAutomaticSvgSizing
                    onClick={() => handleAction(onAddToNote)}
                    variant="ghost"
                    className="w-full justify-start gap-2 text-sm"
                  >
                    <ListPlus className="size-4 text-text-tertiary" />
                    <TypographyP2 className="text-text-primary">{t('chat.addToNote')}</TypographyP2>
                  </Button>
                )}

                {/* Replace note/document */}
                {hasPermissionToReplace && (
                  <Button size="lg"
                    disableAutomaticSvgSizing
                    onClick={() => handleAction(onReplaceNote)}
                    variant="ghost"
                    className="w-full justify-start gap-2 text-sm"
                  >
                    <Replace className="size-4 text-text-tertiary" />
                    <TypographyP2 className="text-text-primary">
                      {t('chat.replaceNote')}
                    </TypographyP2>
                  </Button>
                )}

                {/* Create new tab */}
                <Button size="lg"
                  disableAutomaticSvgSizing
                  onClick={() => handleAction(onCreateNewTab)}
                  variant="ghost"
                  className="w-full justify-start gap-2 text-sm"
                >
                  <FilePlus className="size-4 text-text-tertiary" />
                  <TypographyP2 className="text-text-primary">
                    {t('chat.createNewTab')}
                  </TypographyP2>
                </Button>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Signup modal for logged-out users */}
      {shouldShowActionsForPreview && (
        <SignUpPromptModalChat
          isOpen={showSignUpModal}
          onClose={() => setShowSignUpModal(false)}
          title={t('chat.signUpToAccessActions')}
          subtitle={t('chat.signUpToAccessActionsSubtitle')}
        />
      )}
    </>
  );
};

export const ComplianceErrorMessage = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}): JSX.Element => {
  const intl = useIntl();
  const displayMessage = resolveTimelineErrorDisplay(message, (descriptor) =>
    intl.formatMessage(descriptor)
  );
  const tryAgainLabel = intl.formatMessage({
    id: 'h76/LU2lTl',
    defaultMessage: 'Try again',
    description: 'REG-51 / SWR-35 recovery button on Evidence query errors',
  });

  return (
    <div
      className="w-full space-y-2 rounded bg-transparent"
      data-testid="query-error-message"
    >
      <div className="flex items-center gap-2">
        <div className="w-full">
          <p className="mb-2 break-words text-sm">{displayMessage}</p>
        </div>
      </div>

      {onRetry && (
        <div className="flex flex-row justify-start">
          <Button
            variant="outline"
            size="md"
            onClick={() => void onRetry()}
            className="shrink-0 transition-colors"
            aria-label={tryAgainLabel}
            data-testid="query-error-try-again"
          >
            {tryAgainLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

// Response typography — scale, line heights and both densities — lives in
// `@heidi/answer-kit`, and the same system drives Work. Density is the host's
// surface role (published through `ProseDensityProvider`), not `sessionId` and
// not the column's width.

export const TextResponse = ({
  sessionId,
  chunks,
  className,
  conversation,
  position,
  isStreaming = false,
  onTryWithResearch,
  mode,
  isLastTextResponse = false,
  isLastInTextGroup = true, // Default true so non-grouped responses show action bar
  isLastTextGroupInAnswer = false,
  groupContent,
  chatTitle,
  density: densityProp,
}: TextResponseProps): JSX.Element => {
  const { t } = useTranslations();
  const isMobile = useIsMobile();
  const inlineImagesEnabled = useIsEvidenceInlineImagesEnabled();
  const isMutedKeySafetyNotesEnabled = useIsMutedKeySafetyNotesEnabled();
  const kindeRegion = useIsUKOrEU();
  const isKeySafetyNotesMuted = shouldMuteKeySafetyNotes({
    flagEnabled: isMutedKeySafetyNotesEnabled,
    region: kindeRegion.region,
    isPending: kindeRegion.isPending,
    isError: kindeRegion.isError,
  });

  // Three sources, most specific first: an explicit prop, then the density the
  // host published, then `sessionId` as a last-resort guess for a response
  // rendered outside any density-publishing host. `Chat` publishes one, so the
  // guess is only reached by an embedder that has not adopted the provider.
  const hostDensity = useProseDensity(sessionId ? 'compact' : 'comfortable');
  const density: ProseDensity = densityProp ?? hostDensity;

  // TODO: Empty text blocks sometimes render as empty divs. Likely caused by citation-only
  // chunks that get stripped, or whitespace-only content. Consider: `if (!message.trim()) return null;`

  // Extract text and citations from chunks
  const message = chunks.map((chunk) => chunk.text).join('');

  // Use groupContent for copy/actions if provided (EVIDENCE mode with multiple text blocks)
  const actionableContent = groupContent || message;

  // EVI-1567 / EV-023: when the backend flags that this answer references a
  // drug, surface a prominent drug-interaction warning above the response.
  // Suppressed when Key Safety Notes is present — that block carries the same
  // caveat inside <keysafetynotes> (EVI-1627 supersedes EVI-1567).
  const hasKeySafetyNotes = /<keysafetynotes[\s>]/i.test(actionableContent);
  const showDrugInteractionWarning =
    chunks.some((chunk) => chunk.drugReferenced) && !hasKeySafetyNotes;

  // EVI-608: Check if response contains user notes - if so, hide response action buttons
  // (actions are shown in the UserNotesBlock instead to avoid duplication)
  // Use actionableContent to check grouped responses in EVIDENCE mode
  // Check both raw tag and preprocessed tag (with data-raw attribute)
  const hasUserNotes =
    actionableContent.includes('<usernotes>') ||
    actionableContent.includes('<usernotes ');

  // Collect annotations and calculate cumulative positions
  // Backend sends end_index: -1 to mean "end of this chunk"
  const annotations = useMemo(() => {
    const result: Annotation[] = [];
    let cumulativeLength = 0;

    for (const chunk of chunks) {
      const chunkLength = chunk.text.length;
      const chunkAnnotations = chunk.annotations || [];

      for (const ann of chunkAnnotations) {
        // -1 means "end of this chunk", otherwise use the provided index
        const adjustedEndIndex =
          ann.end_index === -1
            ? cumulativeLength + chunkLength
            : cumulativeLength + ann.end_index;

        result.push({
          ...ann,
          end_index: adjustedEndIndex,
          start_index:
            cumulativeLength + (ann.start_index === -1 ? chunkLength : ann.start_index),
        });
      }

      cumulativeLength += chunkLength;
    }

    return result;
  }, [chunks]);

  // Fix malformed markdown citations before rendering
  const cleanedMessage = useMemo(
    () => removeOuterMarkdownCitationParenthesis(message),
    [message]
  );

  // Check mode and citations
  // AUTO mode can be classified to EVIDENCE on the backend, so we treat AUTO as EVIDENCE for rendering.
  // If AUTO → LEGACY, the chunks won't have citations anyway, so this is safe.
  const isEvidenceMode = mode === ChatMode.EVIDENCE || mode === ChatMode.AUTO;
  const hasInlineCitations = isEvidenceMode && chunks.some((c) => c.citations?.length);

  // Extract web search sources from conversation timeline (these have page_age)
  const webSearchSources = useMemo((): Source[] => {
    if (!conversation?.timeline) return [];
    return conversation.timeline
      .filter(
        (entry): entry is Extract<typeof entry, { type: 'web_search_item' }> =>
          entry.type === 'web_search_item' && entry.status === 'completed'
      )
      .flatMap((entry) => entry.sources);
  }, [conversation?.timeline]);

  // Build enriched references for the References Section
  // EVI-605: Backend now sends enriched citation metadata directly
  const enrichedReferences = useMemo(
    () =>
      hasInlineCitations
        ? getEnrichedReferences(chunks, webSearchSources)
        : [],
    [hasInlineCitations, chunks, webSearchSources]
  );

  // Build concatenated text with citation markers for EVIDENCE mode
  const { fullText: markedText, citationMap } = useMemo(
    () =>
      hasInlineCitations
        ? buildTextWithCitationMarkers(chunks)
        : { fullText: '', citationMap: new Map<number, CitationSource[]>() },
    [hasInlineCitations, chunks]
  );

  // Store citationMap in a ref so streamdownComponents can read latest values
  // without needing citationMap in its dependency array.
  // This prevents cite-badge component identity from changing on every stream update,
  // which would cause CitationBadge to unmount/remount and lose its isOpen state.
  const citationMapRef = useRef(citationMap);
  citationMapRef.current = citationMap;

  // Build image map from conversation timeline for evidence-image rendering
  const imageMap = useMemo(() => {
    const map = new Map<number, EvidenceImage>();
    if (!inlineImagesEnabled || !conversation?.timeline) return map;
    for (const entry of conversation.timeline) {
      if (entry.type === 'web_search_item' && entry.images?.length) {
        for (const img of entry.images) {
          if (!map.has(img.n)) {
            map.set(img.n, img);
          }
        }
      }
    }
    return map;
  }, [inlineImagesEnabled, conversation?.timeline]);

  const imageMapRef = useRef(imageMap);
  imageMapRef.current = imageMap;

  // Refs so streamdownComponents can read these without depending on them.
  // `conversation` gets a new ref every streaming chunk; depending on it would
  // re-create every custom element handler and remount their blocks per chunk
  // — flickered KeyPointsBlock on hover.
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;
  const positionRef = useRef(position);
  positionRef.current = position;

  // Smooth stream animation for text rendering
  const smoothedText = useSmoothStream(
    hasInlineCitations ? markedText : cleanedMessage,
    isStreaming
  );

  // EVI-608: Extract <usernotes> content and encode as data attribute to preserve \n
  // before Streamdown parses it as markdown. This allows UserNotesBlock to use TipTap
  // with the raw text (including newlines) instead of already-parsed React children.
  // EVI-731: Process usernotes for ALL modes since mode selector has been removed from UI.
  // The backend auto-router can still classify queries as LEGACY, but users should always
  // see the Draft Note section with action buttons regardless of routed mode.
  const processedText = useMemo(() => {
    let result = smoothedText;

    result = result.replace(/<keypoints>\n\n+/g, '<keypoints>\n');
    result = result.replace(/\n\n+<\/keypoints>/g, '\n</keypoints>');

    // Same single-block constraint for <safetycallout>: a blank line right
    // after the opening tag (common for multi-paragraph / bulleted safety
    // content) would close the HTML block early, leaving the callout empty and
    // dropping the content back into plain prose — defeating the EV-023 /
    // QA-173 prominence control. Collapse the leading/trailing blank lines.
    result = result.replace(/<safetycallout>\n\n+/g, '<safetycallout>\n');
    result = result.replace(/\n\n+<\/safetycallout>/g, '\n</safetycallout>');

    // Same single-block constraint for the trailing <keysafetynotes> summary
    // (EVI-1627): any blank line inside the tag closes the HTML block early.
    result = normalizeKeySafetyNotesTags(result);
    result = collapseCustomBlockBlankLines(result, 'keysafetynotes');

    // Match complete tags <usernotes>...</usernotes>
    result = result.replace(
      /<usernotes>([\s\S]*?)<\/usernotes>/g,
      (_match: string, content: string) => {
        const encoded = btoa(encodeURIComponent(content));
        return `<usernotes data-raw="${encoded}"></usernotes>`;
      }
    );

    // Match incomplete/streaming tags <usernotes>... (no closing tag yet)
    // This allows content to stream in while preserving \n
    result = result.replace(
      /<usernotes>(?!.*<\/usernotes>)([\s\S]*)$/,
      (_match: string, content: string) => {
        const encoded = btoa(encodeURIComponent(content));
        return `<usernotes data-raw="${encoded}">`;
      }
    );

    // Last, so it sees the text Streamdown will actually parse. Citations on
    // this path are inline `cite-badge` markers rather than offsets, so the
    // rewrite needs no index map.
    return normalizeHeadingDepth(result);
  }, [smoothedText]);

  // Build a display-ordered index for inline images so captions and body
  // references read as "Image 1, Image 2, …" based on the order images
  // appear in the answer (scholarship convention), not the order they were
  // retrieved from the search backend. Also rewrites any stale "Image N"
  // body references the LLM emitted using the retrieval index — belt-and-
  // braces against LLM drift from the prompt.
  const { displayText, displayIndexMap } = useMemo(() => {
    const map = new Map<number, number>();
    if (!isEvidenceMode || imageMap.size === 0) {
      return { displayText: processedText, displayIndexMap: map };
    }

    const tagRegex = /<evidence-image\s+data-n="(\d+)"[^>]*>/gi;
    let displayIdx = 0;
    let match: RegExpExecArray | null;
    while ((match = tagRegex.exec(processedText)) !== null) {
      const n = Number(match[1]);
      if (!map.has(n)) {
        displayIdx += 1;
        map.set(n, displayIdx);
      }
    }

    let rewritten = processedText;
    for (const [n, di] of map.entries()) {
      if (n === di) continue;
      const ref = new RegExp(`\\b(image)(\\s+)${n}\\b`, 'gi');
      rewritten = rewritten.replace(
        ref,
        (_full, imgWord: string, ws: string) => `${imgWord}${ws}${di}`,
      );
    }

    return { displayText: rewritten, displayIndexMap: map };
  }, [processedText, isEvidenceMode, imageMap]);

  const displayIndexMapRef = useRef(displayIndexMap);
  displayIndexMapRef.current = displayIndexMap;

  // Build markdown components based on mode
  // Evidence: always use evidenceMdComponents (unified styling)
  // Legacy: use mdComponents (unchanged for backward compatibility)
  const streamdownComponents = useMemo(() => {
    const baseComponents = isEvidenceMode
      ? getEvidenceMdComponents(density)
      : mdComponents;

    // Custom components for Evidence mode
    const customComponents: Record<string, unknown> = {};

    // Shared variant derivation for both callout blocks (KeyPointsBlock and
    // KeySafetyNotes). Follows the prose density so the card's padding and title
    // scale with the column it sits in, rather than being a second read of
    // `sessionId` that could drift from the body typography around it.
    const calloutVariant = densityToCalloutVariant(density);

    // Callout type density must match the prose beside it, so it is derived
    // from exactly the condition that applies `outOfSessionOverrides` below —
    // `isEvidenceMode && !sessionId`. Reading `sessionId` alone would ask for
    // the 16px card in a non-Evidence answer whose prose stayed at 14px.
    const calloutDensity: 'compact' | 'comfortable' =
      isEvidenceMode && !sessionId ? 'comfortable' : 'compact';

    // Add keypoints handler for Evidence mode
    // The LLM outputs <keypoints>...</keypoints> tags which get parsed by rehype-raw
    // and rendered using KeyPointsBlock component with nested markdown parsing
    // NOTE: Element name must NOT contain hyphens - Streamdown's block parser regex (\w+)
    // doesn't match hyphens, causing content to be split incorrectly across blocks (EVI-509)
    // EVI-553: Pass citationMapRef so citations work inside keypoints section
    // Each callout's `<cite-badge>` renderer is built here: the surface_block,
    // theme, partner fill and className are per-surface and DOM-visible, and
    // the citation map is Chat's.
    const keyPointsCiteBadge = hasInlineCitations
      ? ({ 'data-idx': dataIdx }: { 'data-idx'?: string }) => {
          const idx = Number(dataIdx);
          const sources = citationMapRef.current?.get(idx);
          if (!sources?.length) return null;
          return (
            <CitationBadge
              sources={sources}
              chatId={conversationRef.current?.metadata?.clientChatId}
              messageId={extractMessageId(conversationRef.current, positionRef.current)}
              sessionId={sessionId ?? undefined}
              // EVI-1486 round 4: key_points surface_block + 1-based citation
              // ordinal so prose vs. summary clicks are distinguishable.
              surfaceBlock="key_points"
              citationNumber={Number.isFinite(idx) ? idx + 1 : undefined}
              // Info-toned chrome so the badge stays legible against the
              // blue card; details live in CitationBadge.
              surfaceTheme="key-points"
            />
          );
        }
      : undefined;

    if (isEvidenceMode) {
      customComponents['keypoints'] = (props: { children?: React.ReactNode }) => (
        <KeyPointsBlock
          isStreaming={isStreaming}
          citeBadgeComponent={keyPointsCiteBadge}
          variant={calloutVariant}
          density={calloutDensity}
        >
          {props.children}
        </KeyPointsBlock>
      );
    }

    // EVI-608 + EVI-731: Add usernotes handler for ALL modes to render user's notes
    // with preserved formatting. The LLM outputs <usernotes>...</usernotes> tags when
    // quoting user's clinical notes. Previously gated on isEvidenceMode, but since the
    // mode selector was removed from the UI, the auto-router can still classify queries
    // as LEGACY - users should always see the Draft Note block with action buttons.
    // Raw content is passed via data-raw attribute (base64 encoded) to preserve \n
    // sessionId and mode are passed for copy/insert actions on just the notes content
    customComponents['usernotes'] = (props: { 'data-raw'?: string }) => (
      <UserNotesBlock
        rawContent={props['data-raw']}
        sessionId={sessionId}
        mode={mode}
        chatTitle={chatTitle}
        chatId={conversationRef.current?.metadata?.clientChatId}
        messageId={extractMessageId(conversationRef.current, positionRef.current)}
      />
    );

    // Inline CTA buttons emitted by the LLM as <heidi-button href="…">…</heidi-button>.
    // The href is re-validated against an allowlist inside HeidiButton — disallowed
    // URLs render as inert text. Registered for all modes (billing / pricing / help
    // CTAs are not Evidence-specific).
    customComponents['heidi-button'] = (props: {
      href?: string;
      children?: React.ReactNode;
    }) => (
      <HeidiButton
        href={props.href}
        sessionId={sessionId}
        chatId={conversationRef.current?.metadata?.clientChatId}
        messageId={extractMessageId(conversationRef.current, positionRef.current)}
      >
        {props.children}
      </HeidiButton>
    );

    // Prominent clinical safety / drug-interaction callout (EV-023 / QA-173).
    // The LLM wraps high-risk medication, interaction, and dosage cautions in
    // <safetycallout>…</safetycallout>; we render them in a warning-toned banner
    // so they stand out from regular evidence prose. Registered on the
    // Streamdown path (Evidence + legacy markdown); the legacy annotated-text
    // path does not promote custom tags (same limitation as keypoints/usernotes).
    // cite-badge markers inside the callout render when inline citations are present.
    const safetyCalloutCiteBadge = hasInlineCitations
      ? ({ 'data-idx': dataIdx }: { 'data-idx'?: string }) => {
          const idx = Number(dataIdx);
          const sources = citationMapRef.current?.get(idx);
          if (!sources?.length) return null;
          return (
            <CitationBadge
              sources={sources}
              chatId={conversationRef.current?.metadata?.clientChatId}
              messageId={extractMessageId(conversationRef.current, positionRef.current)}
              sessionId={sessionId ?? undefined}
              surfaceBlock="safety_callout"
              citationNumber={Number.isFinite(idx) ? idx + 1 : undefined}
              className="ml-0.5 align-middle"
            />
          );
        }
      : undefined;

    customComponents['safetycallout'] = (props: {
      children?: React.ReactNode;
    }) => (
      <SafetyCallout
        isStreaming={isStreaming}
        citeBadgeComponent={safetyCalloutCiteBadge}
      >
        {props.children}
      </SafetyCallout>
    );

    // Trailing "Key Safety Notes" summary block emitted by the backend as
    // <keysafetynotes>…</keysafetynotes> (EVI-1627). Its presence is gated
    // server-side by release-evidence-enable-key-safety-notes; only the muted
    // styling is gated here (`shouldMuteKeySafetyNotes`).
    const keySafetyNotesCiteBadge = hasInlineCitations
      ? ({ 'data-idx': dataIdx }: { 'data-idx'?: string }) => {
          const idx = Number(dataIdx);
          const sources = citationMapRef.current?.get(idx);
          if (!sources?.length) return null;
          return (
            <CitationBadge
              sources={sources}
              chatId={conversationRef.current?.metadata?.clientChatId}
              messageId={extractMessageId(conversationRef.current, positionRef.current)}
              sessionId={sessionId ?? undefined}
              surfaceBlock="key_safety_notes"
              citationNumber={Number.isFinite(idx) ? idx + 1 : undefined}
              opaquePartnerFill
              // The muted card is `bg-background-tertiary`, which is the
              // badge's own default fill — without lifting the badge onto
              // `surface` and giving it a real border it disappears into the
              // block. The certified amber card needs no lift.
              className={cn(
                'ml-0.5 align-middle',
                isKeySafetyNotesMuted && 'border-border bg-surface hover:bg-surface-hover'
              )}
            />
          );
        }
      : undefined;

    customComponents['keysafetynotes'] = (props: {
      children?: React.ReactNode;
    }) => (
      <KeySafetyNotes
        isStreaming={isStreaming}
        citeBadgeComponent={keySafetyNotesCiteBadge}
        variant={calloutVariant}
        density={calloutDensity}
        muted={isKeySafetyNotesMuted}
      >
        {props.children}
      </KeySafetyNotes>
    );

    // Add cite-badge handler only when citations exist
    if (hasInlineCitations) {
      customComponents['cite-badge'] = ({
        'data-idx': dataIdx,
      }: {
        'data-idx'?: string;
      }) => {
        const idx = Number(dataIdx);
        // Read from ref to get latest citationMap without recreating this callback
        const sources = citationMapRef.current.get(idx);
        if (!sources?.length) return null;
        return (
          <CitationBadge
            sources={sources}
            chatId={conversationRef.current?.metadata?.clientChatId}
            messageId={extractMessageId(conversationRef.current, positionRef.current)}
            sessionId={sessionId ?? undefined}
            // EVI-1486 round 4: this is the prose surface_block; data-idx is
            // the 0-based citation marker so we expose it as a 1-based ordinal.
            surfaceBlock="prose"
            citationNumber={Number.isFinite(idx) ? idx + 1 : undefined}
            className="ml-0.5 align-middle"
          />
        );
      };
    }

    // Register evidence-image handler for block image display + lightbox.
    // `data-n` is the retrieval index (key into imageMap). The caption renders
    // the *display* index so "Image 1" = first image shown in the answer,
    // matching body references after rewriting (scholarship convention).
    // Gated on the flag only — NOT `imageMap.size` — so images that arrive
    // mid-stream still render (handler reads latest map via `imageMapRef`).
    if (isEvidenceMode && inlineImagesEnabled) {
      customComponents['evidence-image'] = ({
        'data-n': dataN,
      }: {
        'data-n'?: string;
      }) => {
        const n = Number(dataN);
        const image = imageMapRef.current.get(n);
        if (!image?.url) return null;
        const displayN = displayIndexMapRef.current.get(n) ?? n;
        const imageForDisplay =
          displayN === image.n ? image : { ...image, n: displayN };
        return <EvidenceImageBlock image={imageForDisplay} />;
      };
    }

    // Return base components merged with custom components
    if (Object.keys(customComponents).length > 0) {
      return {
        ...baseComponents,
        ...customComponents,
      } as unknown as MarkdownComponents;
    }

    return baseComponents as MarkdownComponents;
  },
  // `conversation` / `position` intentionally not deps — read via refs in
  // `heidi-button` to keep handler identities stable across streaming chunks.
  // (Not flagged by `react-hooks/exhaustive-deps` because the memo body reads
  // `conversationRef`/`positionRef`, not `conversation`/`position` directly.)
  [
    isEvidenceMode,
    hasInlineCitations,
    isStreaming,
    sessionId,
    mode,
    chatTitle,
    inlineImagesEnabled,
    density,
    isKeySafetyNotesMuted,
  ]);

  // Convert annotations to citations format for SourcesButton (for non-EVIDENCE modes)
  const citations = annotations.map((annotation, index) => ({
    id: index,
    title: annotation.title,
    url: annotation.url,
    favicon: getFaviconUrl(annotation.url),
  }));

  // Real action handlers using existing Ask Heidi functionality
  // Use actionableContent for grouped EVIDENCE mode responses
  const { onAddToNote, onReplaceNote, onAddNewTab } = useChatResponseActions(
    actionableContent,
    mode,
    sessionId,
    chatTitle,
    conversation?.metadata?.clientChatId,
    extractMessageId(conversation, position)
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(actionableContent);
      toast({
        tone: 'success',
        title: 'Copied to clipboard',
      });
    } catch (error) {
      logger.error('Failed to copy chat message to clipboard', {
        error,
        messageLength: actionableContent?.length,
      });
      toast({
        tone: 'destructive',
        title: 'Error',
        description: 'Failed to copy',
      });
    }
  };

  // EVI-1486: provide chat/message identity to deeply-nested children
  // (esp. the static `mdComponents.a` / `evidenceMdComponents.a` bindings)
  // so inline citation/source clicks can attach `chat_id` / `message_id`
  // to `evidence_source_clicked` without prop-drilling through Streamdown.
  const chatMessageContextValue = useMemo(
    () => ({
      chatId: conversation?.metadata?.clientChatId,
      messageId: extractMessageId(conversation, position),
      sessionId: sessionId ?? undefined,
      defaultSurface: 'inline_markdown_link' as const,
    }),
    [conversation, position, sessionId]
  );

  return (
    <ChatMessageContext.Provider value={chatMessageContextValue}>
    <div
      className={cn('w-full duration-300 animate-in fade-in', className)}
      data-testid="evidence-response-block"
    >
      <div className="flex items-center gap-2">
        <div
          className="w-full min-w-0 rounded bg-transparent"
          data-streaming={isStreaming ? 'true' : undefined}
          data-testid="evidence-response-content"
        >
          {/* Legacy mode with annotations uses AnnotatedText for badge positioning */}
          {!isEvidenceMode && annotations.length > 0 ? (
            <AnnotatedText
              text={smoothedText}
              annotations={annotations}
              isAnimating={isStreaming}
              // EVI-1486: thread analytics IDs into the legacy-mode prose
              // CitationBadges (Evidence-mode goes through `cite-badge`).
              chatId={conversation?.metadata?.clientChatId}
              messageId={extractMessageId(conversation, position)}
              sessionId={sessionId ?? undefined}
            />
          ) : (
            // MermaidStreamingContext lets the custom Mermaid `errorComponent`
            // (configured in `streamdown-plugins.ts`) suppress transient
            // parse-error flickers while a Mermaid block is still streaming.
            <MermaidStreamingContext.Provider value={isStreaming}>
              <Streamdown
                components={streamdownComponents}
                // The relational spacing rules go on Streamdown's own wrapper so
                // the markdown blocks are its direct children — one selector
                // level, and `space-y-0` replaces the library's 16px default
                // rather than being fought from an ancestor.
                className={isEvidenceMode ? responseProseClass(density) : undefined}
                isAnimating={isStreaming}
                mode={isStreaming ? undefined : 'static'}
                controls={{ table: true, code: true }}
                codeBlockMaxHeight={0}
                plugins={streamdownPlugins}
                mermaid={mermaidConfig}
                shikiTheme={shikiTheme}
                rehypePlugins={streamdownRehypePlugins}
              >
                {displayText}
              </Streamdown>
            </MermaidStreamingContext.Provider>
          )}
        </div>
      </div>

      {/* EVI-1567 / EV-023: render the disclaimer once per answer, at the end of
          the answer's text and before its references section. An EVIDENCE answer
          can be split into multiple contiguous text groups (text -> tool call ->
          text) and every text_item at/after the signal is flagged, so gate on
          "last text group of the answer" to show the banner exactly once.
          - Legacy grouped renderer: isLastInTextGroup marks the last block.
          - V2 block renderer: passes isLastInTextGroup={false} (chrome hoisted
            to group level) and supplies isLastTextGroupInAnswer instead. */}
      {showDrugInteractionWarning &&
        !isStreaming &&
        (isLastInTextGroup || isLastTextGroupInAnswer) && (
        <DrugInteractionWarningBanner />
      )}

      {/* actions bar - only show when not streaming AND is last in text group AND no user notes */}
      {/* EVI-608: When user notes are present, action buttons are shown in UserNotesBlock instead */}
      {!isStreaming && isLastInTextGroup && !hasUserNotes && (
        <div className="mt-2 flex flex-row items-center justify-between gap-2">
          {/* LEFT SIDE: CSAT */}
          <div className="flex flex-row items-center gap-1">
            <CustomerFeedback
              conversation={conversation}
              position={position}
              mode={mode}
            />
          </div>

          {/* RIGHT SIDE: Actions + Copy */}
          <div className="flex flex-row items-center gap-0">
            {/* Show ActionsPopover for session-based chat or sessionless chat */}
            <ActionsPopover
              onAddToNote={onAddToNote}
              onReplaceNote={onReplaceNote}
              onCreateNewTab={onAddNewTab}
              isSessionless={isEmpty(sessionId)}
            />

            <div className="flex flex-row items-center gap-0">
              {/* Try with Research button - only show for most recent LEGACY response */}
              {mode === ChatMode.LEGACY && isLastTextResponse && onTryWithResearch && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        disableAutomaticSvgSizing
                        variant="ghost"
                        size="md"
                        iconOnly
                        onClick={() => void onTryWithResearch()}
                        className="transition-colors hover:bg-background-tertiary-hover"
                        aria-label={t('tooltips.tryWithResearch')}
                      >
                        <BookOpenIcon className="size-4 text-text-secondary/90" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {t('tooltips.tryWithResearch')}
                      <TooltipArrow />
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {/* Copy button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      disableAutomaticSvgSizing
                      variant="ghost"
                      size="md"
                      iconOnly
                      onClick={() => void handleCopy()}
                      className="transition-colors hover:text-text-primary"
                      aria-label="Copy message"
                    >
                      <CopyIcon className="size-4 text-text-secondary" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {t('tooltips.copy')}
                    <TooltipArrow />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      )}
      {/* Sources button - only show for non-EVIDENCE modes with citations */}
      {!hasInlineCitations && citations.length > 0 && (
        <div className="flex items-center gap-2">
          <SourcesButton citations={citations} />
        </div>
      )}

      {/* References Section - collapsible bibliography for EVIDENCE mode */}
      {/* TODO: Inline citation → reference linking:
          - Clicking a citation number should scroll to the corresponding reference
          - On hover, show a popover with all occurrences of that source in the response
            (e.g., if PubMed is cited at positions 1 and 5, show both snippets)
      */}
      {hasInlineCitations && (
        <ReferencesSection
          references={enrichedReferences}
          chatId={conversation?.metadata?.clientChatId}
          sessionId={sessionId ?? undefined}
          messageId={extractMessageId(conversation, position)}
          isStreaming={isStreaming}
          defaultExpanded={!sessionId && !isMobile}
          className="mt-6"
          compact={!!sessionId}
        />
      )}
    </div>
    </ChatMessageContext.Provider>
  );
};

// ===== SOURCES BUTTON COMPONENT =====
// Simple sources button for citations

interface SourcesButtonProps {
  citations: Citation[];
  className?: string;
}

export const SourcesButton = ({
  citations,
  className,
}: SourcesButtonProps): JSX.Element | null => {
  const [isOpen, setIsOpen] = useState(false);

  if (!citations.length) return null;

  return (
    <div className={cn('relative inline-block', className)}>
      <Button
        disableAutomaticSvgSizing
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="text-xs"
      >
        <BookOpenIcon className="mr-1 size-3" />
        {citations.length} source{citations.length !== 1 ? 's' : ''}
      </Button>

      {isOpen && (
        <div className="absolute bottom-full left-0 z-10 mb-2 w-64 rounded-lg border bg-background-secondary p-3 shadow-lg">
          <TypographyP className="mb-2 font-semibold">Sources</TypographyP>
          <div className="max-h-40 space-y-2 overflow-y-auto">
            {citations.map((citation) => (
              <a
                key={`${citation.id}-${citation.url}`}
                href={citation.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded p-2 text-sm transition-colors hover:bg-surface-hover"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={citation.favicon || ''}
                  alt=""
                  className="size-4 rounded-sm"
                  onError={(e) => {
                    // Fallback to BookOpenIcon if favicon fails to load
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'block';
                  }}
                />
                <BookOpenIcon className="hidden size-4" />
                <TypographyP className="min-w-0 flex-1 truncate text-xs text-text-link">
                  {citation.title}
                </TypographyP>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
